import { workspaceInterface } from '@ardoq/workspace-interface';
import { ArdoqId, GroupingRule, FilterAttributes } from '@ardoq/api-types';
import { DynamicPerspectiveFilter } from '@ardoq/perspectives';
import { Filter, PerspectiveBackboneModel } from 'aqTypes';
import { perspectives as Perspectives } from 'collections/perspectives';
import { contextInterface } from 'modelInterface/contextInterface';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { omit } from 'lodash';
import { maybeBackboneFiltersToPureData } from 'collections/toPureDataFilterUtils';
import {
  loadPerspective,
  clearPerspectives,
} from 'collections/helpers/loadPerspective';
import { currentUserInterface } from '../currentUser/currentUserInterface';

type SavedPerspectiveFiltersWithBackboneContext = {
  /**
   * for loaded saved perspective we have FilterAttributes[], but once we save it, it becomes aware of Backbone context and switches to Filter[]
   */
  advancedFilters: FilterAttributes[] | Filter[];
  /**
   * for loaded saved perspective we have FilterAttributes, but once we save it, it becomes aware of Backbone context and switches to Filter
   */
  workspaceFilter: null | FilterAttributes | Filter;
};

export type SavedPerspectiveWithBackboneContext = {
  description: string | null;
  _id: ArdoqId;
  lastModifiedBy: ArdoqId;
  workspace: ArdoqId;
  createdBy: ArdoqId;
  lastModifiedByName: string;
  filters: SavedPerspectiveFiltersWithBackboneContext;
  name: string;
  createdByEmail: string;
  created: string;
  dynamicFilters: DynamicPerspectiveFilter[];
  groupBys: GroupingRule[];
  lastUpdated: string;
  lastModifiedByEmail: string;
  ardoqPersistent: {
    useCase: string;
  } | null;
  _version: number;
  ardoq: {
    entityType: 'perspective';
  };
  createdByName: string;
};

type SavedPerspectiveFilters = {
  advancedFilters: FilterAttributes[];
  workspaceFilter: null | FilterAttributes;
};

export type SavedPerspective = Omit<
  SavedPerspectiveWithBackboneContext,
  'filters'
> & { filters: SavedPerspectiveFilters };

type SavedPerspectivePermissions = {
  create: boolean;
  delete: boolean;
  update: boolean;
};

export type SavedPerspectivesPermissions = Map<
  string,
  SavedPerspectivePermissions
>;

const getAll = (): SavedPerspective[] => {
  return Perspectives.map(perspective => removeBackboneContext(perspective));
};

const getById = (perspectiveId: string): SavedPerspective | null => {
  const savedPerspective = Perspectives.get(perspectiveId);
  return savedPerspective ? removeBackboneContext(savedPerspective) : null;
};

const removeBackboneContext = (
  perspective: PerspectiveBackboneModel
): SavedPerspective => {
  const savedPerspectiveAttributes: SavedPerspectiveWithBackboneContext =
    perspective.attributes;
  const basicFields = omit(savedPerspectiveAttributes, 'filters');

  const { filters } = savedPerspectiveAttributes;

  return {
    ...basicFields,
    filters: maybeBackboneFiltersToPureData(filters),
  };
};

const removePerspective = (perspectiveId: string) => {
  const perspectiveToDelete = Perspectives.get(perspectiveId); // // TODO, AL will add a check if id exists or else throw error
  return perspectiveToDelete?.destroy({
    wait: true,
  });
};

const getSavedPerspectivesPermissions = () => {
  const permissions = getAll().map(perspective => {
    const perspectivePermissions =
      getSavedPerspectivesPermissionsForPerspective(perspective._id);
    return [perspective._id, perspectivePermissions] as const;
  });

  return new Map(permissions);
};

const getSavedPerspectivesPermissionsForPerspective = (
  savedPerspectiveId: string
) => {
  const isOrgAdmin = isCurrentUserOrgAdmin();
  const isOrgWriter = isCurrentUserOrgWriter();
  const isOwnerOfThePerspective =
    isCurrentUserAnOwnerOfPerspective(savedPerspectiveId);

  return {
    create: isOrgAdmin || isOrgWriter,
    delete: isOrgAdmin || (isOwnerOfThePerspective && isOrgWriter),
    update: isOrgAdmin || (isOwnerOfThePerspective && isOrgWriter),
  };
};

const isCurrentUserOrgAdmin = () => {
  return currentUserInterface.currentUserIsOrgAdmin();
};

const isCurrentUserOrgWriter = () => {
  return workspaceInterface.hasWriteAccess(
    contextInterface.getActiveWorkspaceId() as string
  );
};

const hasUserPermissionToSavePerspectives = (): boolean => {
  return isCurrentUserOrgAdmin() || isCurrentUserOrgWriter();
};

const isCurrentUserAnOwnerOfPerspective = (perspectiveId: string) => {
  const createdUserId = Perspectives.get(perspectiveId)?.attributes.createdBy;
  const currentUserId = currentUserInterface.getCurrentUserId();
  return currentUserId ? createdUserId === currentUserId : false;
};

const loadSavedPerspective = async (savedPerspectiveId: string) => {
  const selectedSavedPerspective = Perspectives.get(savedPerspectiveId); // TODO, AL will add a check if id exists or else throw error
  await loadPerspective(selectedSavedPerspective);
};

const getSelectedSavedPerspectiveId = () => {
  return Perspectives.getCurrentSet()?.id;
};

const isSavedPerspectiveSelected = () => {
  return Boolean(Perspectives.getCurrentSet()?.id);
};

const saveExistingPerspective = (_: ArdoqId) => {
  /** TODO
   * We have proposed an idea to modify the update perspective function which would use the selectedPerspectiveId,
   * but currently we are not focusing on doing that and going with existing function
   * however we still want to pass the perspectiveId here and to resolve the lint error AL has used _
   */
  return Perspectives.updateCurrentPerspective(
    groupingRuleInterface.getActiveGroupingRules()
  );
};

/*
  It saves formatting, grouping and filtering rules to currently applied perspective,
  AM proposed to rewrite this method in the future, but for now we can at least explain the intention
*/
const saveAsNewPerspective = (perspectiveName: string) => {
  return Perspectives.createFromCurrentModifiers({
    name: perspectiveName,
    workspaceId: contextInterface.getActiveWorkspaceId() as string,
    setAsCurrent: true,
    groupBys: groupingRuleInterface.getActiveGroupingRules(),
  });
};

const createNewPerspective = (perspectiveName: string) => {
  return Perspectives.createFromCurrentModifiers({
    name: perspectiveName,
    workspaceId: contextInterface.getActiveWorkspaceId() as string,
    setAsCurrent: true,
    groupBys: groupingRuleInterface.getActiveGroupingRules(),
  });
};

const renameSavedPerspective = (
  newSavedPerspectiveName: string,
  selectedSavedPerspectiveId: string
) => {
  const savedPerspectiveToBeRenamed = Perspectives.get(
    selectedSavedPerspectiveId
  );
  if (!savedPerspectiveToBeRenamed) {
    throw new Error(
      'Perspective does not exist, perhaps someone else has deleted it before you.'
    );
  }
  return savedPerspectiveToBeRenamed.save(
    { name: newSavedPerspectiveName },
    { wait: true }
  );
};

/**
 * `perspectiveInterface` is a Backbone-era singleton, and one whose source of
 * truth has not moved yet: saved perspectives still live in the Backbone
 * collection `perspectives` (`collections/perspectives.ts`). `perspectives$`
 * (`streams/perspectives/perspectives$.ts`) exists but is a
 * `defer(perspectiveApi.fetch) + shareSWR()` fetch stream -- it has no reduced
 * state, no `byId` index and no `.state`, so it is not a drop-in replacement.
 *
 * Until the collection is replaced, treat the derived streams under
 * `streams/perspectiveEditorData/` as the boundary that owns the read, join them
 * at a permitted boundary -- a `*$.ts` file, a file inside a `*$/` folder, or a
 * `*routines.ts` -- and thread the resolved perspectives downstream as plain
 * parameters. The mutating members should be replaced by dispatching the owning
 * action and letting `perspectives/routines.ts` do the work.
 *
 * Every member below is deprecated. See
 * `packages/eslint-plugin-ardoq-lint/docs/rules/no-model-interface-call.md`
 * for the hoisting recipe.
 */
export const perspectiveInterface = {
  /**
   * @deprecated Join a derived stream under `streams/perspectiveEditorData/` rather than reading the Backbone collection at call time.
   */
  getAll,
  /**
   * @deprecated Join a derived stream under `streams/perspectiveEditorData/` rather than reading the Backbone collection at call time.
   */
  getById,
  /**
   * @deprecated Dispatch the owning action and let `perspectives/routines.ts` do the work.
   */
  clearPerspectives: () =>
    clearPerspectives({ shouldTriggerChangeEvent: true }),
  /**
   * @deprecated Dispatch the owning action and let `perspectives/routines.ts` do the work.
   */
  removePerspective,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call the matching `permissionsOperations` predicate (`@ardoq/access-control`).
   */
  getSavedPerspectivesPermissions,
  /**
   * @deprecated Join `currentUserPermissionContext$` and call the matching `permissionsOperations` predicate (`@ardoq/access-control`).
   */
  hasUserPermissionToSavePerspectives,
  /**
   * @deprecated Dispatch the owning action and let `perspectives/routines.ts` apply the perspective.
   */
  loadSavedPerspective,
  /**
   * @deprecated Join `activeFilter$` (`streams/filters/activeFilter$.ts`) and read `selectedPerspectiveId`.
   */
  getSelectedSavedPerspectiveId,
  /**
   * @deprecated Join `activeFilter$` (`streams/filters/activeFilter$.ts`) and compare `selectedPerspectiveId`.
   */
  isSavedPerspectiveSelected,
  /**
   * @deprecated Dispatch the owning action and let `perspectives/routines.ts` do the work.
   */
  saveExistingPerspective,
  /**
   * @deprecated Dispatch the owning action and let `perspectives/routines.ts` do the work.
   */
  saveAsNewPerspective,
  /**
   * @deprecated Dispatch the owning action and let `perspectives/routines.ts` do the work.
   */
  createNewPerspective,
  /**
   * @deprecated Dispatch the owning action and let `perspectives/routines.ts` do the work.
   */
  renameSavedPerspective,
};
